import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import Hero from "../../components/Hero/Hero";
import SearchBar from "../../components/SearchBar/SearchBar";
import GMBScoring from "../../components/GMBScoring/GMBScoring";
import ReCAPTCHA from "react-google-recaptcha";
import toast from "../../components/ui/toast";
import { parseBusinessFromDBToValidFormat } from "../../utils/utils";
import { Helmet } from "react-helmet";
import "./audit.css";
import InfoStats from "./InfoStats";
import ModalMoreInfo from "./ModalMoreInfo";
import PageFooter from "../../components/PageFooter/PageFooter";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4000/tech";

const LOADING_STEPS = [
  { delay: 100, text: "Vérification de votre fiche Google Business Profile" },
  { delay: 5000, text: "Analyse de votre e-réputation et de vos avis" },
  { delay: 10000, text: "Analyse de la concurrence locale" },
  { delay: 16000, text: "Évaluation de votre positionnement local" },
  { delay: 25000, text: "Finalisation de votre rapport d'audit" },
];

const Home = () => {
  const [GMBInfo, setGMBInfo] = useState();
  const [displayGMBSection, setDisplayGMBSection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [loadingStep, setLoadingStep] = useState(0);
  const [hasFullAccess, setFullAccess] = useState(false);
  const [auditError, setAuditError] = useState(null);
  const recaptchaRef = useRef();
  const [businessAddress, setBusinessAddress] = useState("");
  const [modalBusinessInfo, setModalBusinessInfo] = useState(false);
  const [isSA, setIsSA] = useState(false);
  const loadingTimers = useRef([]);
  let location = useLocation();

  useEffect(() => {
    if (location.search.includes("access=localranker")) {
      setIsSA(true);
    }
  }, []);

  useEffect(() => {
    return () => {
      loadingTimers.current.forEach(clearTimeout);
    };
  }, []);

  const handleOpenModalBusinessInfo = () => {
    if (isLoading) return;
    setModalBusinessInfo(true);
  };

  const resetState = () => {
    setIsLoading(false);
    setLoadingMessage("");
    setLoadingStep(0);
    loadingTimers.current.forEach(clearTimeout);
    loadingTimers.current = [];
  };

  const submitBusiness = async (businessAddress, businessInfo) => {
    if (isLoading) return;

    try {
      setModalBusinessInfo(false);
      setAuditError(null);
      setDisplayGMBSection(false);
      setGMBInfo(undefined);

      const tokenCaptcha = isSA ? true : await recaptchaRef.current.executeAsync();
      if (!tokenCaptcha) {
        toast.warning("Veuillez résoudre le captcha pour accéder à l'audit");
        return;
      }

      setIsLoading(true);

      loadingTimers.current.forEach(clearTimeout);
      loadingTimers.current = [];
      LOADING_STEPS.forEach((step, i) => {
        const timer = setTimeout(() => {
          setLoadingMessage(step.text);
          setLoadingStep(i);
        }, step.delay);
        loadingTimers.current.push(timer);
      });

      const response = await fetch(
        `${API_URL}/gmb-audit?businessAddress=${encodeURIComponent(businessAddress)}${
          businessInfo
            ? `&businessInfo=${encodeURIComponent(JSON.stringify(businessInfo))}`
            : isSA
              ? "&sa=true"
              : ""
        }`,
        { method: "POST" },
      );

      if (response.status === 429) {
        setAuditError("Vous avez atteint la limite d'essais. Réessayez plus tard ou contactez-nous.");
        resetState();
        recaptchaRef.current?.reset();
        return;
      }

      const data = await response.json();

      if (data === "Local Business Not Found") {
        setAuditError("Établissement introuvable. Vérifiez le nom et l'adresse, puis réessayez.");
        resetState();
      } else if (data === "Error" || data?.err) {
        setAuditError(
          data?.err === true
            ? "Email non valide. Veuillez réessayer avec un email professionnel."
            : "Une erreur est survenue lors de l'analyse. Veuillez réessayer.",
        );
        resetState();
      } else {
        const resp = await parseBusinessFromDBToValidFormat(data);
        setGMBInfo(resp);
        setFullAccess(!!isSA);
        setDisplayGMBSection(true);
        resetState();
      }
      recaptchaRef.current?.reset();
    } catch (e) {
      if (e?.message?.includes("Too many") || e?.message?.includes("429")) {
        setAuditError("Vous avez atteint la limite d'essais. Réessayez plus tard ou contactez-nous.");
      } else {
        setAuditError("Une erreur est survenue. Veuillez réessayer dans quelques instants.");
      }
      resetState();
      recaptchaRef.current?.reset();
    }
  };

  return (
    <div>
      <Helmet>
        <title>Audit fiche Google My Business | LocalRanker</title>
        <meta
          name="description"
          content="Auditez gratuitement votre fiche GMB et commencez les optimisations pour dépasser vos concurrents !"
        />
        <link rel="canonical" href="https://localranker.tech/audit-google-my-business" />
        <meta property="og:title" content="Audit fiche Google My Business | LocalRanker" />
        <meta property="og:description" content="Auditez gratuitement votre fiche GMB et commencez les optimisations pour dépasser vos concurrents !" />
        <meta property="og:url" content="https://localranker.tech/audit-google-my-business" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Localranker" />
      </Helmet>
      <Hero
        title="Outil d'audit fiche Google Business Profile"
        subtitle="Analysez gratuitement votre fiche GBP en 1 clic : score d'optimisation, e-reputation, concurrence locale. Identifiez vos axes d'amélioration et dépassez vos concurrents dans le Local Pack."
        ctaText="Lancer mon audit gratuit"
        ctaOnClick={() => document.querySelector('.search-form')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
      />
      <SearchBar
        handleOpenModalBusinessInfo={handleOpenModalBusinessInfo}
        isLoading={isLoading}
        setBusinessAddress={setBusinessAddress}
        businessAddress={businessAddress}
        submitBusiness={submitBusiness}
        isSa={isSA}
      />

      {isLoading && (
        <div className="audit-loader">
          <div className="audit-loader__bar">
            <div className="audit-loader__bar-fill" />
          </div>
          <p className="audit-loader__text">{loadingMessage}</p>
          <div className="audit-loader__steps">
            {LOADING_STEPS.map((_, i) => (
              <span
                key={i}
                className={`audit-loader__dot${i <= loadingStep ? " audit-loader__dot--active" : ""}`}
              />
            ))}
          </div>
        </div>
      )}

      {auditError && !isLoading && (
        <div className="audit-error">
          <div className="audit-error__icon">!</div>
          <p className="audit-error__text">{auditError}</p>
          <button
            className="audit-error__retry"
            onClick={() => setAuditError(null)}
          >
            Réessayer
          </button>
        </div>
      )}

      <ReCAPTCHA
        sitekey="6LcLC9UZAAAAAOY_vgaU__i3Qhs1tmHtvbibjD2V"
        size="invisible"
        ref={recaptchaRef}
      />
      {!displayGMBSection && !auditError && !isLoading && <InfoStats />}
      <GMBScoring
        hasFullAccess={hasFullAccess}
        displayGMBSection={displayGMBSection}
        googleInfo={GMBInfo}
      />
      <ModalMoreInfo
        modalBusinessInfo={modalBusinessInfo}
        setModalBusinessInfo={setModalBusinessInfo}
        submitBusiness={submitBusiness}
        businessAddress={businessAddress}
      />
      <section className="seo-content">
        <div className="seo-content__inner">
          <h2>Pourquoi réaliser un audit Google Business Profile ?</h2>
          <p>
            Votre fiche Google Business Profile (anciennement Google My Business) est le premier point de contact entre votre établissement et vos futurs clients. Lorsqu'un internaute recherche un commerce, un restaurant ou un prestataire à proximité, Google affiche les fiches locales directement dans le <strong>Local Pack</strong> et sur Google Maps. Une fiche incomplète, mal optimisée ou négligée, c'est une opportunité manquée de capter du trafic qualifié vers votre point de vente.
          </p>
          <p>
            Notre outil d'audit GBP gratuit analyse en quelques secondes les critères déterminants pour votre référencement local : complétude de la fiche, qualité des photos, fréquence des publications, e-réputation, cohérence NAP (Nom, Adresse, Téléphone) et bien plus. Vous obtenez un score global ainsi que des recommandations concrètes pour améliorer votre visibilité locale.
          </p>

          <h2>Les critères analysés par notre audit SEO local</h2>
          <p>
            L'audit évalue votre fiche sur plus d'une vingtaine de critères regroupés en plusieurs catégories. La <strong>complétude de la fiche</strong> vérifie que vos informations essentielles sont renseignées : horaires d'ouverture, catégorie principale et secondaires, description de l'établissement, lien vers votre site web et numéro de téléphone. L'analyse de <strong>l'e-réputation</strong> mesure votre note moyenne, le nombre d'avis et la présence de réponses aux avis clients, un facteur clé pour convaincre de nouveaux visiteurs.
          </p>
          <p>
            L'outil vérifie également la présence de <strong>Google Posts</strong> récents (actualités, offres, événements), la qualité et la quantité de vos <strong>photos et vidéos</strong>, l'existence de <strong>produits ou services</strong> référencés, ainsi que la section <strong>Questions &amp; Réponses</strong>. Chaque critère est noté individuellement avec une observation détaillée et une recommandation actionnable.
          </p>

          <h2>Comment améliorer votre score d'optimisation GBP ?</h2>
          <p>
            Après avoir lancé votre audit, vous recevez un rapport complet avec les points forts validés et les axes d'amélioration prioritaires. Les actions les plus impactantes pour votre référencement local sont généralement : compléter toutes les informations de votre fiche, publier régulièrement du contenu via les Google Posts, répondre systématiquement aux avis (positifs comme négatifs), ajouter des photos récentes de qualité et activer les attributs pertinents pour votre activité.
          </p>
          <p>
            Pour aller plus loin, <a href="https://localranker.fr/demander-demo?utm_source=localranker.tech&utm_medium=audit-google-my-business" target="_blank" rel="noreferrer noopener">Localranker</a> vous permet de gérer et d'optimiser l'ensemble de vos fiches Google Business Profile depuis une seule plateforme : publication multi-fiches, gestion centralisée des avis, suivi du positionnement local et rapports de performance automatisés. Que vous gériez un seul point de vente ou un réseau d'établissements, l'audit est la première étape pour reprendre le contrôle de votre présence locale sur Google.
          </p>
        </div>
      </section>
      <PageFooter />
    </div>
  );
};

export default Home;
